import React from "react";

import { TextClamp } from "../components/text/TextClamp";
import { TEXT_CONSTRAINTS } from "../foundations/text-constraints";
import { sanitizeNarrativeText } from "../foundations/text-sanitize";
import { CoverBackground } from "../primitives/CoverBackground";
import { SlideFrame } from "../layouts/SlideFrame";
import { EditableText } from "./EditableText";
import { registerSlide } from "./registry";

import type { EditableSlideProps } from './contracts'
import type { CoverSlideData } from "./renderer-internal-types";

const COVER_BG = "var(--ds-cover-bg)";
const COVER_TEXT = "var(--ds-white)";

function CoverSlideComponent(props: CoverSlideData & EditableSlideProps) {
  const {
    title,
    subtitle,
    clientLogoUrl,
    authorName,
    studyType,
    meta,
    footerText,
    editable,
    onEdit,
  } = props;

  const resolvedLogoUrl = clientLogoUrl ?? null;
  const resolvedTitle = sanitizeNarrativeText(title);
  const normalizedSubtitle = (subtitle || "").replace(/\s+/g, " ").trim();
  const titleLength = (resolvedTitle || "").replace(/\s+/g, " ").trim().length;
  const titleFontScale =
    titleLength > 120 ? 0.56 : titleLength > 80 ? 0.68 : titleLength > 50 ? 0.74 : 1;
  const titleClamp = TEXT_CONSTRAINTS.coverTitle;
  const subtitleClamp = TEXT_CONSTRAINTS.coverSubtitle;
  const footerClamp = TEXT_CONSTRAINTS.coverFooter;
  const coverMetaLabelClamp = TEXT_CONSTRAINTS.coverMetaLine;
  const coverMetaValueClamp = TEXT_CONSTRAINTS.coverMeta;

  const displayLabels = props.displayLabels;
  const metaItems: Array<{
    key: string;
    value: string | null | undefined;
    label: string;
    testId?: string;
  }> = [
    // legacy reader only: the builder stamps these three `displayLabels` (study type only beside its value).
    {
      key: "date",
      value: meta?.date,
      label: displayLabels?.date || "Date",
    },
    {
      key: "authorName",
      value: authorName,
      label: displayLabels?.createdBy || "Created by",
    },
    {
      key: "studyType",
      value: studyType,
      // legacy reader only: see above.
      label: displayLabels?.studyType || "Study type",
      testId: "cover-meta-study-type",
    },
  ].filter((item) => item.value && String(item.value).trim());

  return (
    <SlideFrame fullBleed background={COVER_BG}>
      <CoverBackground />

      <div
        data-testid="cover-border"
        style={{
          position: "absolute",
          inset: 0,
          border: "3px solid var(--ds-cover-border-color)",
          boxSizing: "border-box",
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            padding: "24px 40px 0",
            minHeight: 52,
            boxSizing: "border-box",
          }}
        >
          {resolvedLogoUrl ? (
            <img
              src={resolvedLogoUrl}
              alt=""
              style={{
                height: 28,
                width: "auto",
                maxWidth: 220,
                objectFit: "contain" as const,
              }}
            />
          ) : null}
        </div>

        <div
          style={{
            padding: "0 80px",
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 24,
            }}
          >
            {/* title tight at the top of the group */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              {editable && onEdit ? (
                <EditableText
                  value={resolvedTitle || ''}
                  field="title"
                  editable
                  onEdit={onEdit}
                  tag="h1"
                  style={{
                    fontFamily: "var(--ds-font-display)",
                    fontSize:
                      titleFontScale < 1
                        ? `calc(var(--ds-fs-cover-h1) * ${titleFontScale})`
                        : "var(--ds-fs-cover-h1)",
                    fontWeight: 400,
                    color: COVER_TEXT,
                    maxWidth: 873,
                  }}
                />
              ) : (
                <TextClamp
                  as="h1"
                  minLines={titleClamp.minLines}
                  maxLines={titleClamp.maxLines}
                  lineHeight={titleClamp.lineHeight}
                  descenderBuffer={0.3}
                  style={{
                    fontFamily: "var(--ds-font-display)",
                    fontSize:
                      titleFontScale < 1
                        ? `calc(var(--ds-fs-cover-h1) * ${titleFontScale})`
                        : "var(--ds-fs-cover-h1)",
                    fontWeight: 400,
                    color: COVER_TEXT,
                    maxWidth: 873,
                  }}
                >
                  {resolvedTitle}
                </TextClamp>
              )}
            </div>
            {normalizedSubtitle ? (
              <div data-testid="cover-subtitle">
                {editable && onEdit ? (
                  <EditableText
                    value={normalizedSubtitle}
                    field="subtitle"
                    editable
                    onEdit={onEdit}
                    tag="p"
                    style={{
                      fontSize: "var(--ds-fs-cover-subtitle)",
                      fontWeight: 400,
                      color: "var(--ds-cover-subtitle-on-dark)",
                      maxWidth: 648,
                    }}
                  />
                ) : (
                  <TextClamp
                    minLines={subtitleClamp.minLines}
                    maxLines={subtitleClamp.maxLines}
                    lineHeight={subtitleClamp.lineHeight}
                    style={{
                      fontSize: "var(--ds-fs-cover-subtitle)",
                      fontWeight: 400,
                      color: "var(--ds-cover-subtitle-on-dark)",
                      maxWidth: 648,
                    }}
                  >
                    {normalizedSubtitle}
                  </TextClamp>
                )}
              </div>
            ) : null}
            {metaItems.length > 0 ? (
              <dl
                data-testid="cover-meta-grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: `repeat(${metaItems.length}, minmax(0, max-content))`,
                  columnGap: 48,
                  rowGap: 16,
                  margin: 0,
                  alignItems: "stretch",
                  width: "100%",
                  maxWidth: 960,
                }}
              >
                {metaItems.map((item) => (
                  <div
                    key={item.key}
                    data-testid={item.testId}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                      minWidth: 0,
                    }}
                  >
                    <TextClamp
                      as="dt"
                      minLines={coverMetaLabelClamp.minLines}
                      maxLines={coverMetaLabelClamp.maxLines}
                      lineHeight={coverMetaLabelClamp.lineHeight}
                      style={{
                        fontSize: "var(--ds-fs-cover-meta-label)",
                        fontWeight: 400,
                        color: "var(--ds-cover-meta-label-accent)",
                        margin: 0,
                      }}
                    >
                      {item.label}
                    </TextClamp>
                    <TextClamp
                      as="dd"
                      minLines={coverMetaValueClamp.minLines}
                      maxLines={coverMetaValueClamp.maxLines}
                      lineHeight={coverMetaValueClamp.lineHeight}
                      style={{
                        fontSize: "var(--ds-fs-cover-meta-value)",
                        fontWeight: 500,
                        color: COVER_TEXT,
                        margin: 0,
                      }}
                    >
                      {item.value}
                    </TextClamp>
                  </div>
                ))}
              </dl>
            ) : null}
          </div>
        </div>

        {footerText ? (
          <TextClamp
            minLines={footerClamp.minLines}
            maxLines={footerClamp.maxLines}
            lineHeight={footerClamp.lineHeight}
            style={{
              position: "absolute",
              bottom: "var(--ds-gap-2xl)",
              left: "var(--ds-slide-pad-x)",
              fontSize: "var(--ds-fs-meta)",
              color: "color-mix(in srgb, var(--ds-white) 35%, transparent)",
            }}
          >
            {footerText}
          </TextClamp>
        ) : null}
      </div>
    </SlideFrame>
  );
}

export const CoverSlide = registerSlide("cover", CoverSlideComponent);
